import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Collapse, FormControlLabel, Grid, Skeleton, Switch, Typography } from "@mui/material";
import type { Personne } from "models";
import ElusList from "./ElusList";
import { elusUtil } from "utils";
import Fonctions from "constants/Fonctions";
import { mandatsService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";

interface ConseilMunicipalInformationsProps {
  communeId: string;
}

function ConseilMunicipalInformations({
  communeId,
}: Readonly<ConseilMunicipalInformationsProps>): ReactNode {
  const [conseilMunicipal, setConseilMunicipal] = useState<Array<Personne>>([]);
  const [isDisplayed, setIsDisplayed] = useState(false);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getMandats(): Promise<void> {
      const _mandats = await mandatsService.getByFilter({
        communesIds: [communeId],
        fonctionsIds: [Fonctions.MAIRE, Fonctions.ADJOINT_MAIRE, Fonctions.CONSEILLER_MUNICIPAL],
        isActive: true,
      });

      const _elusConseilMunicipal = elusUtil
        .getElusFromMandats(_mandats)
        .toSorted(elusUtil.sortByName);

      setConseilMunicipal(_elusConseilMunicipal);
    }

    void catchErrors(getMandats);
  }, [communeId, catchErrors]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setIsDisplayed(event.target.checked);
  }

  if (isLoading) {
    return <Skeleton width="100%" />;
  }

  return (
    <Grid container spacing={2} size={12} sx={{ alignItems: "center" }}>
      <Grid size="grow">
        <Typography>
          Conseil Municipal:{" "}
          <Typography component="span" sx={{ fontWeight: "500" }}>
            {conseilMunicipal.length}
          </Typography>{" "}
          membre
          {conseilMunicipal.length > 1 ? "s" : ""}
        </Typography>
      </Grid>
      <Grid size="auto">
        <FormControlLabel
          control={<Switch checked={isDisplayed} onChange={handleChange} />}
          label="Afficher"
        />
      </Grid>
      <Collapse in={isDisplayed}>
        <ElusList elus={conseilMunicipal} />
      </Collapse>
    </Grid>
  );
}

export const ConseilMunicipalInformationsWithErrorBoundary = withComponentErrorBoundary(
  ConseilMunicipalInformations
);
